import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Building2,
  Users,
  Brain,
  Shield,
  Award,
  Globe,
  Server,
  BookOpen,
  Bot,
  GraduationCap,
  MapPin,
  Mail,
  Linkedin
} from 'lucide-react';

// Cerebro Core Logo URL
// Cerebro Core Logo - Teal/Gold Neural Brain Hexagon
const CEREBRO_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

// FAQ Data for structured data and rendering
const FAQ_DATA = [
  {
    id: 1,
    question: "What is Cerebro Core?",
    answer: "Cerebro Core is an enterprise learning and capability development platform focused on Governance, Risk, Compliance (GRC), IT Governance, Cybersecurity, and AI Governance. It forms part of the Navigate Compliance Group and provides structured learning pathways, simulations, and professional development tools for organisations and individuals.",
    icon: Building2
  },
  {
    id: 2,
    question: "Who is Cerebro Core designed for?",
    answer: `Cerebro Core is built for:

• Risk and Compliance professionals
• IT Governance and Cybersecurity teams
• AI Governance leaders
• Enterprise Learning & Development teams
• Boards and executive leadership
• Individual professionals seeking structured capability development

It supports both enterprise organisations and individual subscribers.`,
    icon: Users
  },
  {
    id: 3,
    question: "How does Cerebro Core address the evolving GRC landscape?",
    answer: `The GRC landscape is evolving due to AI adoption, cyber threats, regulatory enforcement, operational resilience requirements, and cross-border complexity.

Cerebro Core responds by offering:

• Regulatory-aligned learning pathways
• AI governance modules
• Crisis and regulator engagement simulations
• Capability maturity diagnostics
• Continuous professional development tools

Learning is designed to build future-ready, regulator-aligned capability.`,
    icon: Shield
  },
  {
    id: 4,
    question: "What skills does Cerebro Core help develop?",
    answer: `Cerebro Core focuses on integrated, modern professional capabilities including:

• Regulatory change management
• AI governance and model risk literacy
• Cyber resilience and operational resilience
• Board-level communication
• Data and technology risk awareness
• Crisis and incident response leadership

The platform builds applied capability, not just theoretical knowledge.`,
    icon: GraduationCap
  },
  {
    id: 5,
    question: "Does Cerebro Core use Artificial Intelligence (AI)?",
    answer: `Yes.

Cerebro Core uses AI to support:

• Personalised learning recommendations
• Pathway optimisation
• Capability diagnostics
• Enterprise analytics

However, Cerebro Core operates under a structured Human-in-the-Loop (HITL) and Human-in-Command model.

AI supports learning, but human oversight governs critical decisions. Professional judgement remains central to all governance-related outputs.`,
    icon: Bot
  },
  {
    id: 6,
    question: "What is Cerebro Core's Human-in-the-Loop (HITL) approach?",
    answer: `Cerebro Core's HITL model ensures that:

• AI supports rather than replaces professional judgement
• Outputs remain transparent and auditable
• Enterprise administrators retain oversight
• Critical interpretations remain under human control

This aligns with responsible AI governance principles and regulatory expectations.`,
    icon: Brain
  },
  {
    id: 7,
    question: "Is Cerebro Core accredited or credit-bearing?",
    answer: `Cerebro Core provides structured learning pathways and pathway completion certificates. These are professional recognition credentials and not academic qualifications unless explicitly delivered in partnership with recognised professional bodies or SETAs.

The platform focuses on practical, industry-aligned capability development.`,
    icon: Award
  },
  {
    id: 8,
    question: "Does Cerebro Core support CPD, WSP, and ATR requirements?",
    answer: `Yes.

Cerebro Core supports:

• Continuous Professional Development (CPD) tracking
• Workplace Skills Plan (WSP) alignment
• Annual Training Report (ATR) support

The platform provides structured reporting, participation tracking, completion analytics, and role-based capability mapping to assist organisations in evidencing structured skills development.`,
    icon: BookOpen
  },
  {
    id: 9,
    question: "Does Cerebro Core work with Professional Bodies and SETAs?",
    answer: `Yes.

Cerebro Core works closely with professional bodies and Sector Education and Training Authorities (SETAs) to:

• Align learning content to recognised standards
• Support professional capability frameworks
• Strengthen governance and digital literacy ecosystems
• Contribute to structured skills development initiatives`,
    icon: Users
  },
  {
    id: 10,
    question: "Where is Cerebro Core hosted?",
    answer: `Cerebro Core is hosted on Amazon Web Services (AWS).

This provides:

• Enterprise-grade security and infrastructure
• Scalability and high availability
• Cloud reliability across jurisdictions
• Compliance-ready hosting environments

AWS hosting supports secure delivery for both enterprise and individual users.`,
    icon: Server
  }
];

// Generate JSON-LD structured data for FAQPage schema
const generateFAQSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer.replace(/\n/g, ' ').replace(/•/g, '-')
      }
    }))
  };
};

// FAQ Item Component
function FAQItem({ faq, isOpen, onToggle }) {
  const Icon = faq.icon;
  
  return (
    <div 
      className={`border rounded-xl transition-all duration-300 ${
        isOpen 
          ? 'border-cerebro-accent/50 bg-navigate-surface' 
          : 'border-cerebro-secondary/30 bg-cerebro-primary/50 hover:border-cerebro-secondary/50'
      }`}
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 text-left"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
            isOpen ? 'bg-cerebro-accent/20' : 'bg-cerebro-secondary/20'
          }`}>
            <Icon className={`h-5 w-5 ${isOpen ? 'text-cerebro-accent' : 'text-cerebro-gold'}`} />
          </div>
          <h3 
            className="text-lg font-semibold text-white pr-4"
            itemProp="name"
          >
            {faq.question}
          </h3>
        </div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
          isOpen ? 'bg-cerebro-accent text-cerebro-primary' : 'bg-cerebro-secondary/30 text-slate-400'
        }`}>
          {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </button>
      
      {isOpen && (
        <div 
          className="px-6 pb-6"
          itemScope
          itemProp="acceptedAnswer"
          itemType="https://schema.org/Answer"
        >
          <div 
            className="pl-14 text-slate-300 leading-relaxed whitespace-pre-line"
            itemProp="text"
          >
            {faq.answer}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const [openFAQ, setOpenFAQ] = useState(1);

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <>
      {/* SEO Meta Tags and Structured Data */}
      <Helmet>
        <title>FAQ - Frequently Asked Questions | Cerebro Core Learning Platform</title>
        <meta name="description" content="Find answers to frequently asked questions about Cerebro Core - the enterprise learning platform for GRC, IT Governance, Cybersecurity, and AI Governance professionals. Learn about features, AI usage, CPD support, and more." />
        <meta name="keywords" content="Cerebro Core FAQ, GRC learning platform, AI governance training, compliance learning, CPD tracking, WSP support, enterprise learning" />
        <link rel="canonical" href="https://cerebro.navcompliance.co.za/faq" />
        
        {/* Open Graph */}
        <meta property="og:title" content="FAQ - Frequently Asked Questions | Cerebro Core" />
        <meta property="og:description" content="Find answers to frequently asked questions about Cerebro Core - the enterprise learning platform for GRC, IT Governance, Cybersecurity, and AI Governance." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cerebro.navcompliance.co.za/faq" />
        
        {/* JSON-LD Structured Data for FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify(generateFAQSchema())}
        </script>
        
        {/* Additional Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Cerebro Core",
            "alternateName": "Cerebro Core Learning Platform",
            "url": "https://cerebro.navcompliance.co.za",
            "logo": CEREBRO_LOGO,
            "description": "Enterprise learning and capability development platform for GRC, IT Governance, Cybersecurity, and AI Governance professionals.",
            "parentOrganization": {
              "@type": "Organization",
              "name": "Navigate Compliance Group",
              "url": "https://www.navigatecompliance.io"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Johannesburg",
              "addressCountry": "ZA"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "hello@navigatelearning.io",
              "contactType": "customer service"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-cerebro-primary">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-cerebro-primary/95 backdrop-blur-xl border-b border-cerebro-secondary/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-3">
                <img src={CEREBRO_LOGO} alt="Cerebro Core" className="h-10 w-auto" />
                <span className="text-white font-bold text-lg hidden sm:block">Cerebro Core</span>
              </Link>
              <div className="hidden md:flex items-center gap-8">
                <Link to="/courses" className="text-slate-400 hover:text-white transition-colors text-sm font-medium">
                  Courses
                </Link>
                <Link to="/about" className="text-slate-400 hover:text-white transition-colors text-sm font-medium">
                  About
                </Link>
                <Link to="/faq" className="text-cerebro-accent text-sm font-medium">
                  FAQ
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/login" className="text-slate-300 hover:text-white text-sm font-medium px-4 py-2">
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-cerebro-primary via-cerebro-secondary/10 to-cerebro-primary" />
            <div className="absolute top-1/3 -right-32 w-[400px] h-[400px] bg-cerebro-accent/5 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cerebro-accent/10 border border-cerebro-accent/30 mb-6">
              <HelpCircle className="h-4 w-4 text-cerebro-accent" />
              <span className="text-sm text-cerebro-accent font-medium">FREQUENTLY ASKED QUESTIONS</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              How Can We <span className="text-cerebro-accent">Help</span>?
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Find answers to common questions about Cerebro Core, our learning platform, 
              AI governance approach, and enterprise capabilities.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16" itemScope itemType="https://schema.org/FAQPage">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Quick Jump Links */}
            <div className="mb-12 p-6 bg-navigate-surface border border-cerebro-secondary/30 rounded-xl">
              <h2 className="text-sm font-semibold text-cerebro-gold uppercase tracking-wider mb-4">
                Quick Navigation
              </h2>
              <div className="flex flex-wrap gap-2">
                {FAQ_DATA.map((faq) => (
                  <button
                    key={faq.id}
                    onClick={() => {
                      setOpenFAQ(faq.id);
                      document.getElementById(`faq-${faq.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      openFAQ === faq.id
                        ? 'bg-cerebro-accent/20 border-cerebro-accent/50 text-cerebro-accent'
                        : 'bg-cerebro-primary/50 border-cerebro-secondary/30 text-slate-400 hover:border-cerebro-secondary/50 hover:text-white'
                    }`}
                  >
                    Q{faq.id}
                  </button>
                ))}
              </div>
            </div>

            {/* FAQ Items */}
            <div className="space-y-4">
              {FAQ_DATA.map((faq) => (
                <div key={faq.id} id={`faq-${faq.id}`}>
                  <FAQItem 
                    faq={faq} 
                    isOpen={openFAQ === faq.id}
                    onToggle={() => toggleFAQ(faq.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Still Have Questions CTA */}
        <section className="py-16 bg-navigate-surface">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Still Have Questions?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Can't find what you're looking for? Our team is here to help you understand 
              how Cerebro Core can support your governance and compliance learning needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:hello@navigatelearning.io?subject=Question about Cerebro Core"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-cerebro-accent text-cerebro-primary font-semibold rounded-lg hover:bg-cerebro-accent/90 transition-colors"
              >
                <Mail className="h-5 w-5" />
                Contact Us
              </a>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-cerebro-secondary text-white font-semibold rounded-lg border border-cerebro-secondary hover:bg-cerebro-secondary/80 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-cerebro-primary border-t border-cerebro-secondary/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid md:grid-cols-4 gap-12">
              <div className="md:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <img src={CEREBRO_LOGO} alt="Cerebro Core" className="h-16 w-auto" />
                  <div className="flex flex-col">
                    <span className="font-heading text-xl font-bold text-white">Cerebro Core</span>
                    <span className="text-[10px] text-zinc-500 tracking-wider uppercase">A Workforce Capability Platform</span>
                  </div>
                </div>
                <p className="text-slate-400 mb-6 max-w-md leading-relaxed">
                  Enterprise Learning & Continuous Professional Development. 
                  Where learning meets intelligence.
                </p>
                <p className="text-sm text-slate-500">
                  Cerebro Core is part of the Navigate Group (Pty) Ltd · Registered in South Africa
                </p>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-4">Platform</h4>
                <ul className="space-y-3">
                  {[
                    { label: 'Courses', href: '/courses' },
                    { label: 'About Us', href: '/about' },
                    { label: 'FAQ', href: '/faq' },
                    { label: 'Pricing', href: '/#pricing' }
                  ].map((link, idx) => (
                    <li key={idx}>
                      <Link to={link.href} className="text-slate-400 hover:text-cerebro-accent transition-colors text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-4">Contact</h4>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-slate-400 text-sm">
                    <MapPin className="h-4 w-4 text-cerebro-accent" />
                    Johannesburg, South Africa
                  </li>
                  <li>
                    <a href="mailto:hello@navigatelearning.io" className="flex items-center gap-2 text-slate-400 hover:text-cerebro-accent transition-colors text-sm">
                      <Mail className="h-4 w-4" />
                      hello@navigatelearning.io
                    </a>
                  </li>
                  <li>
                    <a href="https://www.navigatecompliance.io" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-cerebro-accent transition-colors text-sm">
                      <Globe className="h-4 w-4" />
                      www.navigatecompliance.io
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-cerebro-secondary/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <a href="https://www.linkedin.com/company/navigate-compliance/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cerebro-accent transition-colors flex items-center gap-1 text-sm">
                    <Linkedin className="h-4 w-4" />
                    LinkedIn
                  </a>
                  <span className="text-cerebro-secondary/50">|</span>
                  <Link to="/responsible-ai" className="text-cerebro-accent hover:text-cerebro-accent/80 text-sm">
                    Responsible AI
                  </Link>
                  <span className="text-cerebro-secondary/50">|</span>
                  <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors text-sm">
                    Privacy
                  </Link>
                  <span className="text-cerebro-secondary/50">|</span>
                  <Link to="/terms" className="text-slate-400 hover:text-white transition-colors text-sm">
                    Terms of Use
                  </Link>
                </div>
                <p className="text-sm text-slate-600">
                  © {new Date().getFullYear()} Navigate Compliance Group. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
